import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, Gem } from 'lucide-react';
import API from '../utils/api';
import ProductCard from '../components/ProductCard';

const CATEGORY_OPTIONS = {
  women: [
    { value: 'rings', label: 'Rings' },
    { value: 'earrings', label: 'Earrings' },
    { value: 'necklace', label: 'Necklace' },
    { value: 'kada-bracelet', label: 'Kada Bracelet' },
    { value: 'chain-bracelet', label: 'Chain Bracelet' },
    { value: 'bangles', label: 'Bangles' },
    { value: 'anklets', label: 'Anklets' },
    { value: 'watches', label: 'Watches' },
    { value: 'handchain', label: 'Handchain' },
  ],
  men: [
    { value: 'chains', label: 'Chains' },
    { value: 'kada-bracelet', label: 'Kada Bracelet' },
    { value: 'chain-bracelet', label: 'Chain Bracelet' },
    { value: 'rings', label: 'Rings' },
  ],
};

export default function Collections() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'all');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [sort, setSort] = useState('newest');

  const activeCategories =
    activeSection === 'men'
      ? CATEGORY_OPTIONS.men
      : activeSection === 'women'
      ? CATEGORY_OPTIONS.women
      : [...CATEGORY_OPTIONS.women, ...CATEGORY_OPTIONS.men].filter(
          (cat, index, self) => index === self.findIndex((c) => c.value === cat.value)
        );

  useEffect(() => {
    const section = searchParams.get('section') || 'all';
    const category = searchParams.get('category') || 'all';

    setActiveSection(section);
    setActiveCategory(category);
  }, [searchParams]);

  useEffect(() => {
    async function load() {
      setLoading(true);

      try {
        const params = new URLSearchParams({ sort, limit: 50 });

        if (activeSection !== 'all') {
          params.set('section', activeSection);
        }

        if (activeCategory !== 'all') {
          params.set('category', activeCategory);
        }

        const search = searchParams.get('search');

        if (search) {
          params.set('search', search);
        }

        const { data } = await API.get(`/products?${params.toString()}`);
        const filteredProducts = (data.products || []).filter((p) => {
  const sectionMatch =
    activeSection === 'all' || String(p.section).toLowerCase() === activeSection;

  const categoryMatch =
    activeCategory === 'all' || String(p.category).toLowerCase() === activeCategory;

  return sectionMatch && categoryMatch;
});


const sortedProducts = [...filteredProducts];

const getPrice = (p) =>
  Number(p.discountPrice || p.salePrice || p.price || 0);

if (sort === "price-asc") {
  sortedProducts.sort((a, b) => getPrice(a) - getPrice(b));
}

if (sort === "price-desc") {
  sortedProducts.sort((a, b) => getPrice(b) - getPrice(a));
}

if (sort === "newest") {
  sortedProducts.sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
}

setProducts(sortedProducts);

      } catch {
        setProducts([]);
      }

      setLoading(false);
    }

    load();
  }, [activeSection, activeCategory, sort, searchParams]);

  const updateFilters = (section, category = 'all') => {
  setActiveSection(section);
  setActiveCategory(category);

  const params = new URLSearchParams();

  if (section && section !== 'all') {
    params.set('section', section);
  }

  if (category && category !== 'all') {
    params.set('category', category);
  }

  navigate(`/collections${params.toString() ? `?${params.toString()}` : ''}`);
};

  const title =
    activeSection === 'men'
      ? "Men's Collection"
      : activeSection === 'women'
      ? "Women's Collection"
      : 'All Collections';

  return (
    <div className="page-enter">
      <div
  className="collections-header"
  style={{
          background: 'var(--cream)',
          padding: '60px 24px',
          textAlign: 'center',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <p
          style={{
            fontSize: 11,
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: 'var(--champagne)',
            marginBottom: 12,
          }}
        >
          Browse
        </p>

        <h1
          style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 'clamp(36px,5vw,56px)',
            fontWeight: 300,
          }}
        >
          {title}
        </h1>
      </div>

     <div
  className="collections-filter-bar"
  style={{
          borderBottom: '1px solid var(--border)',
          background: 'white',
          position: 'sticky',
          top: 64,
          zIndex: 50,
        }}
      >
        <div
  className="collections-filter-inner"
  style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div className="collections-filter-buttons" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['all', 'women', 'men'].map((section) => (
              <button
                key={section}
                onClick={() => updateFilters(section, 'all')}
                style={{
                  padding: '7px 16px',
                  borderRadius: 20,
                  border: '1px solid var(--border)',
                  fontSize: 12,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  background: activeSection === section ? 'var(--maroon)' : 'white',
                  color: activeSection === section ? 'white' : 'var(--text-mid)',
                }}
              >
                {section === 'all' ? 'All' : section}
              </button>
            ))}

            <span style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />

            <button
              onClick={() => updateFilters(activeSection, 'all')}
              style={{
                padding: '7px 16px',
                borderRadius: 20,
                border: '1px solid var(--border)',
                fontSize: 12,
                letterSpacing: 1,
                textTransform: 'uppercase',
                cursor: 'pointer',
                background: activeCategory === 'all' ? 'var(--champagne)' : 'white',
                color: activeCategory === 'all' ? 'white' : 'var(--text-mid)',
              }}
            >
              All Categories
            </button>

            {activeCategories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => updateFilters(activeSection, cat.value)}
                style={{
                  padding: '7px 16px',
                  borderRadius: 20,
                  border: '1px solid var(--border)',
                  fontSize: 12,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  background: activeCategory === cat.value ? 'var(--champagne)' : 'white',
                  color: activeCategory === cat.value ? 'white' : 'var(--text-mid)',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="collections-sort" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SlidersHorizontal size={14} color="var(--text-light)" />

            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 2,
                padding: '6px 10px',
                fontSize: 12,
                background: 'var(--ivory)',
              }}
            >
              <option value="newest">Newest First</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>
      </div>

      <div className="collections-grid-wrap" style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
        {loading ? (
          <div className="jewel-loader">
            <div className="jewel-loader-ring" />
            <span>Curating jewellery...</span>
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Gem size={52} color="var(--maroon)" style={{ marginBottom: 16 }} />

            <h3
              style={{
                fontFamily: "'Cormorant Garamond',serif",
                fontSize: 24,
              }}
            >
              No products found
            </h3>

            <p style={{ color: 'var(--text-light)', marginTop: 8 }}>
              Add products in admin panel or change the selected filters.
            </p>
          </div>
    
    ) : (
  <div
    className="collections-products-grid"
    style={{
      display: 'grid',
      gridTemplateColumns:
        window.innerWidth <= 600
          ? 'repeat(2, 1fr)'
          : 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: window.innerWidth <= 600 ? 12 : 24,
    }}
  >
    {products.map((p) => (
      <ProductCard key={p._id} product={p} />
    ))}
  </div>
)}
        
      </div>
      <style>{`
  @media(max-width: 768px) {
    .collections-header {
      padding: 42px 18px !important;
    }

    .collections-filter-bar {
      top: 58px !important;
      overflow-x: auto;
    }

    .collections-filter-inner {
      padding: 12px 14px !important;
      align-items: flex-start !important;
    }

    .collections-filter-buttons {
      width: 100%;
      flex-wrap: nowrap !important;
      overflow-x: auto;
      padding-bottom: 6px;
      -webkit-overflow-scrolling: touch;
    }

    .collections-filter-buttons button {
      flex: 0 0 auto;
      white-space: nowrap;
    }

    .collections-sort {
      width: 100%;
      justify-content: space-between;
    }

    .collections-sort select {
      flex: 1;
    }

    .collections-grid-wrap {
      padding: 24px 14px !important;
    }

    .collections-products-grid {
  grid-template-columns: repeat(2, 1fr) !important;
  gap: 10px !important;
}
  }

  @media (max-width: 420px) {
  .collections-products-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
  }
}
`}</style>
    </div>
  );
}